/**
 * Generic Feature Section for City Landing Pages
 *
 * Displays city-specific features (voting, petitions, group buying, discussions)
 * with optional video demos and modal popups.
 *
 * Usage:
 *   <FeatureSection
 *     cityName="Houston"
 *     title="A Platform Built for Community Power"
 *     subtitle="Tools designed to help Houston residents organize..."
 *     visualSvg="/media_houston/features-visual.svg"
 *     features={houstonFeatures}
 *     enableVideoDemos={true}
 *   />
 */

import React, { useState } from 'react';

export interface Feature {
  id: string;
  icon: string;
  title: string;
  headline: string;
  description: string;

  // Optional video/media
  videoDemo?: string;
  videoFull?: string;
  still?: string;
  gif?: string;
}

export interface FeatureSectionProps {
  // City identity
  cityName: string;

  // Content
  title: string;
  subtitle?: string;
  visualSvg?: string; // Optional SVG graphic for visual interest

  // Features array
  features: Feature[];

  // Media controls
  enableVideoDemos?: boolean; // Default: false (opt-in for video cards)
  enableFullDemoModal?: boolean; // Default: false (opt-in for modal)

  // Custom components (optional)
  FeatureCardComponent?: React.ComponentType<any>;
  ModalComponent?: React.ComponentType<any>;
}

export function FeatureSection(props: FeatureSectionProps) {
  const {
    cityName,
    title,
    subtitle,
    visualSvg,
    features,
    enableVideoDemos = false,
    enableFullDemoModal = false,
    FeatureCardComponent,
    ModalComponent,
  } = props;

  // Modal state (only if enabled)
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get selected feature data
  const selectedFeature = features.find((f) => f.id === selectedFeatureId) || null;

  // Modal handlers
  const openModal = (featureId: string) => {
    if (!enableFullDemoModal) return;
    setSelectedFeatureId(featureId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedFeatureId(null), 300);
  };

  return (
    <section id="features" className="py-12 sm:py-16 md:py-20 bg-muted/30">
      <div className="container px-4 sm:px-6">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center m-0">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-0">{title}</h2>

          {/* Optional Subtitle (placed above visual) */}
          {subtitle && (
            <p className="text-base sm:text-lg text-muted-foreground px-4 leading-relaxed mt-4 p-8">
              {subtitle}
            </p>
          )}

          {/* Optional Visual (SVG/WEBP/etc.) */}
          {visualSvg && (
            <div className="-mt-4 sm:-mt-6 flex justify-center">
              <img
                src={visualSvg}
                alt={`${cityName} community platform features visualization`}
                className="w-full max-w-[1600px] h-auto"
                style={{ minHeight: '240px' }}
                loading="lazy"
                width={1600}
                height={640}
              />
            </div>
          )}
        </div>

        {/* Feature Cards */}
        {enableVideoDemos && FeatureCardComponent ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto mt-8">
            {features.map((feature, index) => (
              <FeatureCardComponent
                key={feature.id}
                feature={feature}
                videoSrc={feature.videoDemo}
                stillSrc={feature.still}
                gifSrc={feature.gif}
                onViewFullDemo={enableFullDemoModal ? () => openModal(feature.id) : undefined}
                className={`feature-card-stagger-${index + 1}`}
              />
            ))}
          </div>
        ) : (
          // Simple feature grid without video
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto mt-8">
            {features.map((feature) => (
              <div
                key={feature.id}
                className="bg-card p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-sm font-medium text-primary mb-3">{feature.headline}</p>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full Demo Modal (optional) */}
      {enableFullDemoModal && ModalComponent && selectedFeature && (
        <ModalComponent
          feature={selectedFeature}
          videoSrc={selectedFeature.videoFull || ''}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </section>
  );
}
